import React, { useState, useEffect, useCallback } from 'react';
import { EventTemplate, EventTemplateSite } from '../../../types';
import { formatCurrency } from '../../../utils/formatters';
import { groupEventService } from '../../../services/groupEventService';
import { authService } from '../../../services/auth';
import EventTemplateForm from './EventTemplateForm';
import EventSiteSelector from './EventSiteSelector';

interface EventTemplateListProps {
  onCreateInstance?: (template: EventTemplate) => void;
}

const EventTemplateList: React.FC<EventTemplateListProps> = ({ onCreateInstance }) => {
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Form modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedTemplate, setSelectedTemplate] = useState<EventTemplate | null>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<EventTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Site selector state
  const [isSiteSelectorOpen, setIsSiteSelectorOpen] = useState(false);
  const [siteSelectionTemplate, setSiteSelectionTemplate] = useState<EventTemplate | null>(null);
  const [templateSites, setTemplateSites] = useState<{ [templateId: number]: EventTemplateSite[] }>({});
  const [loadingSites, setLoadingSites] = useState(false);

  const storeId = authService.getStoreId();

  // Load templates from API
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await groupEventService.getTemplates(storeId || undefined, !showInactive);
      setTemplates(data);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [storeId, showInactive]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const handleEditTemplate = (template: EventTemplate) => {
    setSelectedTemplate(template);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleSaveTemplate = async (templateData: Partial<EventTemplate>) => {
    try {
      if (formMode === 'create') {
        await groupEventService.createTemplate({
          store_id: storeId || 1,
          default_booking_code: templateData.default_booking_code || '',
          name: templateData.name || '',
          description: templateData.description,
          sites_blocked: templateData.sites_blocked ?? true,
          min_nights: templateData.min_nights,
          max_nights: templateData.max_nights,
          max_sites_per_booking: templateData.max_sites_per_booking || 1,
          stay_buffer_days: templateData.stay_buffer_days,
          default_price: templateData.default_price,
          active: templateData.active ?? true,
        });
      } else if (selectedTemplate) {
        await groupEventService.updateTemplate(selectedTemplate.id, {
          default_booking_code: templateData.default_booking_code,
          name: templateData.name,
          description: templateData.description,
          sites_blocked: templateData.sites_blocked,
          min_nights: templateData.min_nights,
          max_nights: templateData.max_nights,
          max_sites_per_booking: templateData.max_sites_per_booking,
          stay_buffer_days: templateData.stay_buffer_days,
          default_price: templateData.default_price,
          active: templateData.active,
        });
      }
      await loadTemplates();
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Failed to save template. Please try again.');
    }
  };

  const handleManageSites = async (template: EventTemplate) => {
    setSiteSelectionTemplate(template);
    setIsSiteSelectorOpen(true);

    // Load existing sites for this template
    try {
      setLoadingSites(true);
      const sites = await groupEventService.getTemplateSites(template.id);
      setTemplateSites(prev => ({
        ...prev,
        [template.id]: sites
      }));
    } catch (err) {
      console.error('Error loading template sites:', err);
    } finally {
      setLoadingSites(false);
    }
  };

  const handleSaveSites = async (siteIds: number[]) => {
    if (!siteSelectionTemplate) return;

    try {
      // Get current assigned site IDs
      const currentSites = templateSites[siteSelectionTemplate.id] || [];
      const currentSiteIds = new Set(currentSites.map(s => s.site_id));
      const newSiteIds = new Set(siteIds);

      // Find sites to add (in new but not in current)
      const sitesToAdd = siteIds.filter(id => !currentSiteIds.has(id));

      // Find sites to remove (in current but not in new)
      const sitesToRemove = currentSites.filter(s => !newSiteIds.has(s.site_id));

      // Add new sites
      if (sitesToAdd.length > 0) {
        await groupEventService.addTemplateSites(siteSelectionTemplate.id, {
          site_ids: sitesToAdd
        });
      }

      // Remove old sites
      for (const site of sitesToRemove) {
        await groupEventService.removeTemplateSite(siteSelectionTemplate.id, site.site_id);
      }

      // Reload templates to get updated site counts
      await loadTemplates();
    } catch (err) {
      console.error('Error saving template sites:', err);
      alert('Failed to update sites. Please try again.');
    }
  };

  const handleDeleteTemplate = (template: EventTemplate) => {
    setDeleteConfirm(template);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      await groupEventService.deleteTemplate(deleteConfirm.id);
      await loadTemplates();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deactivating template:', err);
      alert('Failed to deactivate template. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateInstance = (template: EventTemplate) => {
    if (onCreateInstance) {
      onCreateInstance(template);
    }
  };

  // Create a mock event for the site selector (template doesn't have dates)
  const getSiteSelectorEvent = (template: EventTemplate) => ({
    id: template.id,
    store_id: template.store_id,
    booking_code: template.default_booking_code,
    name: template.name,
    start_date: '',
    end_date: '',
    max_sites_per_booking: template.max_sites_per_booking,
    sites_blocked: template.sites_blocked,
    active: template.active
  });

  if (loading) {
    return (
      <div className="event-template-list">
        <div className="events-loading">Loading templates...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-template-list">
        <div className="events-error">
          {error}
          <button onClick={loadTemplates} className="btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="event-template-list">
      <div className="events-actions">
        <div className="events-actions-left">
          <label className="events-filter">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive templates
          </label>
        </div>
        <button className="btn-create-event" onClick={handleCreateTemplate}>
          + Create Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="events-empty">
          <div className="events-empty-icon">📋</div>
          <div className="events-empty-text">
            No templates found. Create a template for recurring events!
          </div>
        </div>
      ) : (
        <table className="events-table">
          <thead>
            <tr>
              <th>Template Name</th>
              <th>Code</th>
              <th>Default Price</th>
              <th>Stay Limits</th>
              <th>Sites</th>
              <th>Instances</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map(template => (
              <tr key={template.id} className={!template.active ? 'inactive-row' : ''}>
                <td>
                  <div className="event-name">
                    <span className="event-name-link">
                      {template.name}
                      {!template.active && <span className="inactive-badge">Inactive</span>}
                    </span>
                    {template.description && (
                      <div className="template-description" title={template.description}>
                        {template.description.length > 50
                          ? `${template.description.substring(0, 50)}...`
                          : template.description}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <span className="booking-code">{template.default_booking_code}</span>
                </td>
                <td>
                  {template.default_price
                    ? formatCurrency(template.default_price)
                    : '-'}
                </td>
                <td>
                  {template.min_nights && template.max_nights
                    ? `${template.min_nights}-${template.max_nights} nights`
                    : template.min_nights
                    ? `Min ${template.min_nights} nights`
                    : template.max_nights
                    ? `Max ${template.max_nights} nights`
                    : '-'}
                </td>
                <td className="count-cell">
                  {template.site_count || 0}
                </td>
                <td className="count-cell">
                  <span className="template-instances">
                    {template.instance_count || 0}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    {template.active && onCreateInstance && (
                      <button
                        className="btn-action"
                        onClick={() => handleCreateInstance(template)}
                        title="Create new event from this template"
                      >
                        + Event
                      </button>
                    )}
                    <button
                      className="btn-action"
                      onClick={() => handleEditTemplate(template)}
                      title="Edit template"
                    >
                      Edit
                    </button>
                    <button
                      className="btn-action"
                      onClick={() => handleManageSites(template)}
                      title="Manage default sites"
                    >
                      Sites
                    </button>
                    {template.active && (
                      <button
                        className="btn-action danger"
                        onClick={() => handleDeleteTemplate(template)}
                        title="Deactivate template"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Template Form Modal */}
      <EventTemplateForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSaveTemplate}
        template={selectedTemplate}
        mode={formMode}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deactivate Template</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to deactivate <strong>{deleteConfirm.name}</strong>?</p>
              <p className="confirm-warning">
                This template will no longer be available for creating new events.
                Existing events using this template will not be affected.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deactivating...' : 'Deactivate Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site Selector Modal */}
      {siteSelectionTemplate && (
        <EventSiteSelector
          isOpen={isSiteSelectorOpen}
          onClose={() => {
            setIsSiteSelectorOpen(false);
            setSiteSelectionTemplate(null);
          }}
          onSave={handleSaveSites}
          event={getSiteSelectorEvent(siteSelectionTemplate)}
          assignedSites={templateSites[siteSelectionTemplate.id] || []}
          loading={loadingSites}
        />
      )}
    </div>
  );
};

export default EventTemplateList;
